import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useIsMobile } from '@hooks/responsive/useIsMobile';
import { returnFileExtension } from '@utils/regex';

// components
import Icon from '../Icon';
import Typography from '../Typography';
import LoadingSpinner from '../LoadingSpinner';
import { Toast } from '../Toast';

// colors
import { blue, gray, white } from '@styles/Colors';

// 다국어 지원
import { useTranslation } from 'next-i18next';

// constants
import { IconSize, TypoVariant } from '@constants/atoms';
import { MOBILE_MODE } from '@constants/size';
import { Radius } from '@styles/Radius';

// hooks
import useHandleDragAction from '@hooks/interaction/useHandleDragAction';
import { useCreateCdnUrlFetch, useCreatePresignedUrlFetch } from '@hooks/fetch/useSystemFetch';
import { SystemPostPresignedUrlPayload } from 'models/swagger/data-contracts';
import { extractS3KeyFromPresignedUrl } from '@utils/file';

type Props = {
  name?: string;
  cdnKey?: string;
  formFileName?: string;
  target?: SystemPostPresignedUrlPayload['target'];
  handleFileUpload: (file: File, cdnUrl: string, name: string) => void;
  handleFileRemove: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  accept?: string;
  isServerUpload?: boolean;
  width?: number;
  height?: number;
  text1?: string;
  text2?: string;
};

const Layout = styled.div<{ width?: number, height?: number }>`
  width: ${({ width }) => width ? `${width}px` : '100%'};
  height: ${({ height }) => height ? `${height}px` : '100%'};
`;

// fileInput & fileList
const CommonCss = `
  border: 1px dashed ${gray.gray4};
  border-radius: ${Radius.MEDIUM};
  background: ${white};

  @media ${MOBILE_MODE} {
    height: 120px;
  }
`;

const FileInput = styled.button<{ disabled: boolean, width?: number, height?: number }>`
  width: ${({ width }) => width ? `${width}px` : '100%'};
  height: ${({ height }) => height ? `${height}px` : '160px'};
  ${CommonCss}
  text-align: left;
  cursor: pointer;

  display: flex;
  column-gap: 12px;
  align-items: center;
  justify-content: center;
  background-color: ${({ disabled }) => (disabled ? gray.gray1 : white)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  @media ${MOBILE_MODE} {
    flex-direction: column;
    row-gap: 8px;
    text-align: center;
  }
`;

const InputPlaceholderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const IconCircle = styled.div`
  width: 40px;
  height: 40px;
  border: 1px solid ${gray.gray1};
  background: ${gray.gray1};
  border-radius: ${Radius.MAXIMUM};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const FileList = styled.div`
  ${CommonCss}
  padding: 8px;
  overflow-y: auto;
`;

const FileItem = styled.div`
  padding: 8px;
  background: ${blue.blue1};
  border-radius: ${Radius.SMALL};

  display: grid;
  grid-template-columns: 1fr 20px;
  column-gap: 10px;
  align-items: center;
`;

const FileItemText = styled.div`
  overflow-x: hidden;
  text-overflow: ellipsis;
`;

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
`;

const FileRemoveButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
`;

export default function FileInputComponent({
  name = '',
  formFileName,
  target,
  cdnKey,
  handleFileUpload,
  handleFileRemove,
  disabled = false,
  accept = '.jpg, .png, .jpeg, .csv, .xls, .xlsx, .pdf, .zip',
  text1,
  text2,
  width,
  height,
}: Props) {
  const isMobile = useIsMobile();
  const { t } = useTranslation('component');

  const dragRef = useRef<HTMLButtonElement | null>(null);
  const inputFile = useRef<HTMLInputElement>(null);

  // 파일 업로드창 오픈
  const handleFileUploadOpen = () => {
    if (PrsignedUrlLoading || CdnLoading) return;

    inputFile?.current?.click();
  };

  /**
   *  파일 업로드 관련
   */
  const [fileName, setFileName] = useState<string>('');

  const { createPresignedUrlMutate, createPresignedUrlFetchIsLoading: PrsignedUrlLoading } =
    useCreatePresignedUrlFetch();
  const { createCdnUrlMutate, createCdnUrlFetchIsLoading: CdnLoading } = useCreateCdnUrlFetch();

  const fileUpload = (file: File, name: string) => {
    if (!file) return;
    const fileNames = file.name.split('.');

    if (accept.includes(fileNames[fileNames.length - 1])) {
      if (target) {
        createPresignedUrlMutate(
          { target, ext: returnFileExtension(file?.name) },
          {
            onSuccess: (presignedRes) => {
              const url = presignedRes?.data.data;

              if (!url) {
                Toast.error(t('파일 업로드에 실패했습니다. 다시 시도해주세요.'));
                return;
              }

              const key = extractS3KeyFromPresignedUrl(url);

              // CDN Url 생성
              createCdnUrlMutate(
                { url, file },
                {
                  onSuccess: () => {
                    // name setting, key값 전달
                    setFileName(file.name);
                    handleFileUpload(file, key, name);
                  },
                },
              );
            },
          },
        );
      } else {
        setFileName(file.name);
        handleFileUpload(file, '', name);
      }
    }
  };

  const fileRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    handleFileRemove(e);
    setFileName('');
  };

  useHandleDragAction(dragRef, name, fileUpload);

  // cdnKey 삭제 시 이름도 삭제
  useEffect(() => {
    if (!cdnKey) {
      setFileName('');
    }
  }, [cdnKey]);

  // 기존 파일명 setting
  useEffect(() => {
    if (!formFileName || formFileName === fileName) return;

    setFileName(formFileName);
  }, [formFileName]);

  return (
    <Layout>
      {(fileName && !target) || cdnKey ? (
        // 파일 O
        <FileList>
          <FileItem>
            <FileItemText>
              <Typography style={{ whiteSpace: 'nowrap' }} variant={TypoVariant.B3} color={blue.blue4}>
                {fileName}
              </Typography>
            </FileItemText>
            <FileRemoveButton onClick={fileRemove} name={name}>
              <Icon
                name="close"
                fill="transparent"
                stroke={blue.blue4}
                style={{ pointerEvents: 'none' }}
                size={IconSize.SMALL}
              />
            </FileRemoveButton>
          </FileItem>
        </FileList>
      ) : (
        // 파일 X
        <FileInput ref={dragRef} onClick={handleFileUploadOpen} disabled={disabled} type="button" width={width} height={height}>
          <input
            type="file"
            id="fileUpload"
            name={name}
            style={{ display: 'none' }} // label을 이용하여 구현하기에 없애줌
            multiple={false} // 파일 다중선택 허용
            ref={inputFile}
            accept={accept}
            onChange={(e) => {
              if (e.target.files) {
                fileUpload(e?.target.files[0], name);
              }
            }}
            disabled={disabled}
          />
          {PrsignedUrlLoading || CdnLoading ? (
            <LoadingSpinner />
          ) : (
            <InputPlaceholderWrapper>
              <IconWrapper>
                <IconCircle>
                  <Icon name="upload-file" size={IconSize.SMALL} fill="transparent" stroke={gray.gray4} />
                </IconCircle>
              </IconWrapper>
              <TextBox>
                <Typography variant={isMobile ? TypoVariant.B6 : TypoVariant.B4} color={gray.gray4}>
                  {text1 ?? t('fileInput.text1')}
                </Typography>
                <Typography variant={isMobile ? TypoVariant.B6 : TypoVariant.B5} color={gray.gray4}>
                  {accept} {text2 ?? t('fileInput.text2')}
                </Typography>
              </TextBox>
            </InputPlaceholderWrapper>
          )}
        </FileInput>
      )}
    </Layout>
  );
}
